import { useQuery, useQueryClient } from "@tanstack/react-query";

export type WebsocketAlert = {
  id: string;
  title: string;
  description: string;
  variant: "default" | "info" | "success" | "warning" | "error" | "destructive";
  profileId?: number;
  eventType?: string;
  metadata?: Record<string, unknown>;
  timestamp: number;
  read: boolean;
};

// Query keys
export const websocketAlertsKey = ["websocket-alerts"];

// SIG-335 rule 4: identical title+description within this window renders ONE
// toast, whatever upstream produced the duplicates (backend replays, snapshot
// + live overlap, re-render storms). Contract C3: toasts are RARE and
// MEANINGFUL — a stack of identical toasts is never meaningful.
const ALERT_DEDUPE_WINDOW_MS = 30_000;
const recentAlertEmits = new Map<string, number>();

const isDuplicateWithinWindow = (key: string, now: number): boolean => {
  const last = recentAlertEmits.get(key);
  if (last !== undefined && now - last < ALERT_DEDUPE_WINDOW_MS) {
    return true;
  }
  // Prune expired entries so the map cannot grow unbounded in a long session.
  recentAlertEmits.forEach((entryTime, entryKey) => {
    if (now - entryTime >= ALERT_DEDUPE_WINDOW_MS) {
      recentAlertEmits.delete(entryKey);
    }
  });
  recentAlertEmits.set(key, now);
  return false;
};

/** Test-only: reset the dedupe window state between test cases. */
export const __resetAlertDedupeForTests = () => {
  recentAlertEmits.clear();
};

/**
 * Service for managing websocket alerts
 */
export const websocketAlertsService = {
  // Add a new alert to the queue. Returns null when the alert was suppressed
  // as a duplicate inside the dedupe window.
  addAlert: (
    queryClient: ReturnType<typeof useQueryClient>,
    alert: Omit<WebsocketAlert, "id" | "timestamp" | "read">,
  ): WebsocketAlert | null => {
    const now = Date.now();
    // SV-CR-5 (Codex adversarial review 2026-07-20): a title|description-only
    // key made DISTINCT money-risk alerts swallow each other — two follower
    // accounts rejecting the same copied order, or two accounts hitting the
    // same daily-loss wording, produced identical copy and the second was
    // suppressed for 30s. Broad collapse is right for the INFO spam class it
    // was built for; anything else must include its identity so a real second
    // event always renders.
    const md = (alert.metadata ?? {}) as Record<string, unknown>;
    const severity = typeof md.severity === "string" ? md.severity : undefined;
    const kind = typeof md.kind === "string" ? md.kind : undefined;
    const isInfoClass = severity === "info" || kind === "listener_dropped";
    const identity = isInfoClass
      ? ""
      : [
          md.account_id ?? md.broker_account_id ?? "",
          md.order_id ?? md.local_order_id ?? "",
          md.profile_id ?? alert.profileId ?? "",
          alert.eventType ?? "",
          md.tradovate_user_id ?? "",
        ].join("|");
    if (isDuplicateWithinWindow(`${alert.title}|${alert.description}|${identity}`, now)) {
      return null;
    }

    const newAlert: WebsocketAlert = {
      ...alert,
      id: Math.random().toString(36).substring(2, 15),
      timestamp: now,
      read: false,
    };

    queryClient.setQueryData<WebsocketAlert[]>(
      websocketAlertsKey,
      (old = []) => {
        return [...old, newAlert];
      },
    );

    // We no longer create visual toasts here
    // The WebsocketAlerts component will handle creating the appropriate toast

    return newAlert;
  },

  // Mark an alert as read
  markAsRead: (
    queryClient: ReturnType<typeof useQueryClient>,
    alertId: string,
  ) => {
    queryClient.setQueryData<WebsocketAlert[]>(
      websocketAlertsKey,
      (old = []) => {
        return old.map((alert) => {
          if (alert.id === alertId) {
            return { ...alert, read: true };
          }
          return alert;
        });
      },
    );
  },

  // Remove an alert from the queue
  removeAlert: (
    queryClient: ReturnType<typeof useQueryClient>,
    alertId: string,
  ) => {
    queryClient.setQueryData<WebsocketAlert[]>(
      websocketAlertsKey,
      (old = []) => {
        return old.filter((alert) => alert.id !== alertId);
      },
    );
  },

  // Hook to access all alerts
  useAlerts: () => {
    return useQuery<WebsocketAlert[]>({
      queryKey: websocketAlertsKey,
      queryFn: () => {
        return [];
      },
      // Don't refetch on window focus or network reconnection
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });
  },
};
